import { BookingSlot } from '../../api-types/models/Booking';
import { addMinutes, getHours } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { calculateBottomMargin, calculateTopMargin } from '../../utils/booking';
import { Tooltip } from 'react-tooltip';

import './index.css';

interface IProps {
    slot: BookingSlot;
    setSelectedSlot: (slot: BookingSlot) => void;
    slotStartHour: number;
    parallelIndex?: number;
    parallelCount?: number;
}

export function BookingSlotContent({
    slot,
    setSelectedSlot,
    slotStartHour,
    parallelIndex = 0,
    parallelCount = 1
}: Readonly<IProps>) {
    const [cellHeight, setCellHeight] = useState(64);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const startDateTime = slot.from;

    const trailing =
        getHours(addMinutes(startDateTime, slot.duration)) !==
        slotStartHour + 1;
    const ending = new Date(startDateTime).getHours() !== slotStartHour;

    // Set top and bottom margin calculated by slot start datetime minutes
    const marginTop = calculateTopMargin(slot, cellHeight);
    const marginBottom = calculateBottomMargin(slot, cellHeight);

    // Calculate the total height needed to span multiple rows
    const durationInMinutes = slot.duration;
    const heightMultiplier = Math.ceil(durationInMinutes / 60);
    const calculatedHeight =
        cellHeight * heightMultiplier - marginTop - marginBottom;

    useEffect(() => {
        // Observe the <td> (grandparent), not the wrapper div (parent).
        // The wrapper collapses to 0 height because its only child is
        // position:absolute, so height:100% may not resolve in all browsers.
        // The <td> always receives a reliable computed height from the table
        // layout algorithm, giving us the correct per-hour row height.
        const parent = buttonRef.current?.parentElement?.parentElement ?? null;
        if (!parent) return;

        // ResizeObserver fires once the element is laid out and on any subsequent
        // size change – this works correctly in Firefox where clientHeight may
        // still be 0 when a plain useEffect callback runs after paint.
        const observer = new ResizeObserver((entries) => {
            const height = entries[0]?.contentRect.height;
            if (height) setCellHeight(height);
        });

        observer.observe(parent);
        return () => observer.disconnect();
    }, []);

    const tooltip =
        slot.attendees.length === 1
            ? slot.attendees
            : `${slot.attendees[0]} and ${slot.attendees.length - 1} more`;

    const isParallel = parallelCount > 1;
    const columnWidthPercent = 100 / parallelCount;

    return (
        <button
            ref={buttonRef}
            id={slot.slotId}
            className={`booking-content active ${
                ending ? 'following' : undefined
            } ${trailing ? 'trailing' : undefined}`}
            onClick={() => setSelectedSlot(slot)}
            style={{
                marginTop,
                height: calculatedHeight > 0 ? `${calculatedHeight}px` : 'auto',
                maxHeight:
                    calculatedHeight > 0 ? `${calculatedHeight}px` : undefined,
                ...(isParallel
                    ? {
                          left: `${parallelIndex * columnWidthPercent}%`,
                          width: `${columnWidthPercent}%`,
                          right: 'auto'
                      }
                    : {})
            }}
            data-tip={slot.attendees.length > 0 ? tooltip : undefined}
        >
            <div className='icon'>
                <img
                    src={`/images/booking/${slot.room.toLowerCase()}.svg`}
                    alt={slot.room}
                />
            </div>
            {(!ending || (ending && trailing)) && (
                <div className='slot-info'>
                    <p>{`${slot.activityName} ${slot.lead ?? ''}`}</p>
                    <b className='text-elipsis'>
                        {slot.room === 'MASSAGE'
                            ? `${
                                  slot.attendees.length
                                      ? slot.attendees[0]
                                      : 'Free'
                              }`
                            : `${slot.attendees.length} / ${slot.capacity} persons`}
                    </b>
                </div>
            )}
            {slot.attendees.length > 0 && (
                <Tooltip key={`tooltip-${tooltip}`} />
            )}
        </button>
    );
}
